import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { updateReflectionDate } from '../utils/notificationManager';
import { 
  getUserGoals, 
  saveUserGoals, 
  getWeightEntries,
  addWeightEntrySync,
  deleteWeightEntrySync,
  getWeightSettings,
  saveWeightSettings,
  getReflections,
  addReflection,
  deleteReflection,
  getUserPreferences,
  saveUserPreferences
} from '../utils/syncService';

const MyGoals = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showNewGoalForm, setShowNewGoalForm] = useState(false);
  const [showEditGoalForm, setShowEditGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [isAdmin, setIsAdmin] = useState(() => {
    try { return localStorage.getItem('isAdmin') === 'true'; } catch { return false; }
  });

  // Get current user's email for private reflections
  const userEmail = localStorage.getItem('userEmail') || 'anonymous';
  const reflectionKey = `reflectionHistory_${userEmail}`;

  // Reflection entries with history (each entry has date + answers) - PER USER
  const [reflectionHistory, setReflectionHistory] = useState([]);

  const [currentReflection, setCurrentReflection] = useState({});
  const [showReflectionHistory, setShowReflectionHistory] = useState(false);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptAnswer, setPromptAnswer] = useState('');

  // Save reflection history to localStorage with user email key
  useEffect(() => {
    try {
      localStorage.setItem(reflectionKey, JSON.stringify(reflectionHistory));
    } catch (error) {
      console.error('Error saving reflection history:', error);
    }
  }, [reflectionHistory, reflectionKey]);

  // Open prompt modal
  const openPromptModal = (category, prompt) => {
    setSelectedPrompt({ category, prompt });
    setPromptAnswer('');
    setShowReflectionModal(true);
  };

  // Save single reflection entry
  const saveReflectionEntry = async () => {
    if (!promptAnswer.trim()) {
      alert('Please write a reflection before saving.');
      return;
    }

    const entry = {
      id: Date.now(),
      date: new Date().toISOString(),
      category: selectedPrompt.category,
      prompt: selectedPrompt.prompt,
      answer: promptAnswer.trim()
    };

    // Add to local state
    setReflectionHistory([entry, ...reflectionHistory]);
    setPromptAnswer('');
    setShowReflectionModal(false);
    updateReflectionDate();
    
    // Sync to Supabase
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      const result = await addReflection(userEmail, entry);
      if (result.success && result.data?.id) {
        // Update local entry with Supabase ID
        setReflectionHistory(prev => prev.map(e => 
          e.id === entry.id ? { ...e, dbId: result.data.id } : e
        ));
      }
    }
    
    alert('✅ Reflection saved!');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // Check URL params for tab
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'weight') {
      setActiveTab('weight');
    }
  }, []);

  // --- Quick Planner (Weekly Focus + Habits) ---
  const [weeklyGoalsV2, setWeeklyGoalsV2] = useState(() => {
    try { return (JSON.parse(localStorage.getItem('goalsV2') || '{}').weeklyGoals) || []; } catch { return []; }
  });
  const [habitsV2, setHabitsV2] = useState(() => {
    try { return (JSON.parse(localStorage.getItem('goalsV2') || '{}').habits) || []; } catch { return []; }
  });
  const [habitChecks, setHabitChecks] = useState(() => {
    try { return (JSON.parse(localStorage.getItem('goalsV2') || '{}').habitChecks) || {}; } catch { return {}; }
  });
  // Detailed Goals (Projects)
  const [detailedGoals, setDetailedGoals] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('goalsV2') || '{}');
      if (Array.isArray(parsed.detailedGoals) && parsed.detailedGoals.length) return parsed.detailedGoals;
    } catch {}
    // Start with empty goals for new members
    return [];
  });
  const [newStepText, setNewStepText] = useState({});
  const [showAddWeekly, setShowAddWeekly] = useState(false);
  const [showAddHabit, setShowAddHabit] = useState(false);
  const [expandedGoals, setExpandedGoals] = useState({});
  const [showAddMenu, setShowAddMenu] = useState(false);

  const toggleGoalExpand = (goalId) => {
    setExpandedGoals(prev => ({ ...prev, [goalId]: !prev[goalId] }));
  };
  const [weeklyForm, setWeeklyForm] = useState({ title: '', dueDate: '' });
  const [habitForm, setHabitForm] = useState({ name: '' });

  // Weight Tracking - will load from Supabase
  const [weightEntries, setWeightEntries] = useState([]);
  const [weightGoalType, setWeightGoalType] = useState('lose');
  const [weightStarting, setWeightStarting] = useState('');
  const [weightGoalTarget, setWeightGoalTarget] = useState('');
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [newWeight, setNewWeight] = useState({ value: '', date: new Date().toISOString().split('T')[0], notes: '' });

  // Sync weight settings to Supabase when they change
  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail && (weightGoalType || weightStarting || weightGoalTarget)) {
      saveWeightSettings(userEmail, {
        goalType: weightGoalType,
        startingWeight: weightStarting,
        targetWeight: weightGoalTarget
      });
    }
    // Also keep localStorage for dashboard
    try {
      localStorage.setItem('weightTracking', JSON.stringify(weightEntries));
      localStorage.setItem('weightGoalType', weightGoalType);
      localStorage.setItem('weightStarting', weightStarting);
      localStorage.setItem('weightGoalTarget', weightGoalTarget);
      window.dispatchEvent(new CustomEvent('weightUpdated', { detail: { entries: weightEntries } }));
    } catch {}
  }, [weightGoalType, weightStarting, weightGoalTarget, weightEntries]);

  const addWeightEntry = async () => {
    if (!newWeight.value || isNaN(parseFloat(newWeight.value))) return;
    const entry = {
      id: Date.now(),
      weight: parseFloat(newWeight.value),
      date: newWeight.date,
      notes: newWeight.notes.trim()
    };
    
    // Add to local state
    setWeightEntries(prev => [entry, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date)));
    setNewWeight({ value: '', date: new Date().toISOString().split('T')[0], notes: '' });
    setShowWeightModal(false);
    
    // Sync to Supabase
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      const result = await addWeightEntrySync(userEmail, entry);
      if (result.success && result.data?.id) {
        // Update local entry with Supabase ID for deletion
        setWeightEntries(prev => prev.map(e => 
          e.id === entry.id ? { ...e, dbId: result.data.id } : e
        ));
      }
    }
  };

  const deleteWeightEntry = async (id) => {
    if (!window.confirm('Delete this weight entry?')) return;
    const entryToDelete = weightEntries.find(e => e.id === id);
    
    // Remove from local state
    setWeightEntries(prev => prev.filter(e => e.id !== id));
    
    // Delete from Supabase if it has a dbId
    if (entryToDelete?.dbId) {
      await deleteWeightEntrySync(entryToDelete.dbId);
    }
  };

  const getWeightProgress = () => {
    if (!weightStarting || !weightGoalTarget) return null;
    const start = parseFloat(weightStarting);
    const target = parseFloat(weightGoalTarget);
    
    // Get current weight from latest entry or use starting weight
    let current = start;
    if (weightEntries.length > 0) {
      const sorted = [...weightEntries].sort((a, b) => new Date(b.date) - new Date(a.date));
      current = sorted[0].weight;
    }
    
    const change = current - start;
    const remaining = target - current;
    const totalToLose = target - start;
    const progressPercent = totalToLose !== 0 ? Math.abs(change / totalToLose) * 100 : 0;
    
    return { start, current, target, change, remaining, progressPercent };
  };

  // Save goals to localStorage AND Supabase
  useEffect(() => {
    const payload = { weeklyGoals: weeklyGoalsV2, habits: habitsV2, habitChecks, detailedGoals };
    localStorage.setItem('goalsV2', JSON.stringify(payload));
    window.dispatchEvent(new Event('goalsUpdated'));
    
    // Sync to Supabase
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      saveUserGoals(userEmail, payload);
    }
  }, [weeklyGoalsV2, habitsV2, habitChecks, detailedGoals]);

  // Load all user data from Supabase on mount
  useEffect(() => {
    const loadAllUserData = async () => {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) return;
      
      // Load goals
      const goalsResult = await getUserGoals(userEmail);
      if (goalsResult.success && goalsResult.data) {
        if (goalsResult.data.weeklyGoals) setWeeklyGoalsV2(goalsResult.data.weeklyGoals);
        if (goalsResult.data.habits) setHabitsV2(goalsResult.data.habits);
        if (goalsResult.data.habitChecks) setHabitChecks(goalsResult.data.habitChecks);
        if (goalsResult.data.detailedGoals) setDetailedGoals(goalsResult.data.detailedGoals);
      }
      
      // Load weight settings
      const weightSettingsResult = await getWeightSettings(userEmail);
      if (weightSettingsResult.success && weightSettingsResult.data) {
        if (weightSettingsResult.data.goal_type) setWeightGoalType(weightSettingsResult.data.goal_type);
        if (weightSettingsResult.data.starting_weight) setWeightStarting(weightSettingsResult.data.starting_weight);
        if (weightSettingsResult.data.target_weight) setWeightGoalTarget(weightSettingsResult.data.target_weight);
      }
      
      // Load weight entries
      const weightEntriesResult = await getWeightEntries(userEmail);
      if (weightEntriesResult.success && weightEntriesResult.data?.length > 0) {
        const entries = weightEntriesResult.data.map(e => ({
          id: e.id,
          dbId: e.id,
          weight: parseFloat(e.weight),
          date: e.date,
          notes: e.notes || ''
        }));
        setWeightEntries(entries);
      }
      
      // Load reflections
      const reflectionsResult = await getReflections(userEmail);
      if (reflectionsResult.success && reflectionsResult.data?.length > 0) {
        const reflections = reflectionsResult.data.map(r => ({
          id: r.id,
          dbId: r.id,
          date: r.created_at,
          category: r.category,
          prompt: r.prompt,
          answer: r.answer
        }));
        setReflectionHistory(reflections);
      }
      
      // Load celebrated milestones from preferences
      const prefsResult = await getUserPreferences(userEmail);
      if (prefsResult.success && prefsResult.data?.celebratedMilestones) {
        try {
          localStorage.setItem('celebratedMilestones', JSON.stringify(prefsResult.data.celebratedMilestones));
        } catch {}
      }
    };
    loadAllUserData();
  }, []);

  // Week helpers (Monday start)
  const today = new Date();
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0,0,0,0);
    return d;
  };
  const weekStart = getWeekStart(today);
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const fmt = (d) => d.toISOString().split('T')[0];

  // Helpers: current week/month counts
  const inCurrentWeek = (dateStr) => {
    const d = new Date(dateStr);
    const ymd = fmt(d);
    return weekDates.map(fmt).includes(ymd);
  };
  const getMonthStart = (date) => { const d = new Date(date); d.setDate(1); d.setHours(0,0,0,0); return d; };
  const monthStart = getMonthStart(today);

  // Compute goal progress from steps or manual percentage
  const getGoalProgress = (goal) => {
    // Derive from steps if available; otherwise use manual progress field
    if (Array.isArray(goal.steps) && goal.steps.length > 0) {
      const done = goal.steps.filter(s => s.completed).length;
      return Math.round((done / goal.steps.length) * 100);
    }
    return typeof goal.progress === 'number' ? goal.progress : 0;
  };

  // Milestone celebration (ensure only once per milestone)
  const [celebration, setCelebration] = useState(null); // { goalTitle, threshold }
  useEffect(() => {
    const checkMilestones = async () => {
      try {
        const seen = JSON.parse(localStorage.getItem('celebratedMilestones') || '{}');
        let updated = false;
        
        detailedGoals.forEach(g => {
          const p = getGoalProgress(g);
          const thresholds = Array.isArray(g.milestones) ? g.milestones : [25,50,75,100];
          thresholds.forEach(t => {
            const key = `${g.id}_${t}`;
            if (p >= t && !seen[key]) {
              // trigger once
              setCelebration({ goalTitle: g.title, threshold: t });
              seen[key] = true;
              updated = true;
            }
          });
        });
        
        localStorage.setItem('celebratedMilestones', JSON.stringify(seen));
        
        // Sync to Supabase if updated
        if (updated) {
          const userEmail = localStorage.getItem('userEmail');
          if (userEmail) {
            const prefsResult = await getUserPreferences(userEmail);
            const currentPrefs = prefsResult.success ? prefsResult.data : {};
            await saveUserPreferences(userEmail, { ...currentPrefs, celebratedMilestones: seen });
          }
        }
      } catch {}
    };
    checkMilestones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailedGoals]);

  // Weekly Focus actions
  const addWeeklyGoal = () => {
    if (!weeklyForm.title.trim()) return;
    setWeeklyGoalsV2(prev => [...prev, { id: Date.now(), title: weeklyForm.title.trim(), dueDate: weeklyForm.dueDate || '', done: false }]);
    setWeeklyForm({ title: '', dueDate: '' });
    setShowAddWeekly(false);
  };
  const toggleWeeklyDone = (id) => {
    setWeeklyGoalsV2(prev => prev.map(g => g.id === id ? { ...g, done: !g.done } : g));
  };
  const deleteWeeklyGoal = (id) => {
    setWeeklyGoalsV2(prev => prev.filter(g => g.id !== id));
  };
  const moveWeeklyGoal = (id, dir) => {
    setWeeklyGoalsV2(prev => {
      const idx = prev.findIndex(g => g.id === id);
      if (idx < 0) return prev;
      const arr = [...prev];
      const swapIdx = idx + (dir === 'up' ? -1 : 1);
      if (swapIdx < 0 || swapIdx >= arr.length) return prev;
      [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
      return arr;
    });
  };

  // Habits actions
  const addHabit = () => {
    if (!habitForm.name.trim()) return;
    setHabitsV2(prev => [...prev, { id: Date.now(), name: habitForm.name.trim() }]);
    setHabitForm({ name: '' });
    setShowAddHabit(false);
  };
  const deleteHabit = (id) => {
    setHabitsV2(prev => prev.filter(h => h.id !== id));
    setHabitChecks(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => { if (key.startsWith(`${id}_`)) delete next[key]; });
      return next;
    });
  };
  const toggleHabit = (habitId, dateStr) => {
    const key = `${habitId}_${dateStr}`;
    setHabitChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Reset this week's habit checkmarks
  const startNewWeek = () => {
    if (!window.confirm('Start a new week? This will clear this week\'s checkmarks for all habits.')) return;
    const weekKeySet = new Set(weekDates.map(d => fmt(d)));
    setHabitChecks(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        const dateStr = key.split('_')[1];
        if (weekKeySet.has(dateStr)) {
          delete next[key];
        }
      });
      return next;
    });
  };
  // Admin mode parse (?admin=on/off)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const adminParam = params.get('admin');
      if (adminParam === 'on') { localStorage.setItem('isAdmin','true'); setIsAdmin(true); }
      else if (adminParam === 'off') { localStorage.setItem('isAdmin','false'); setIsAdmin(false); }
      else { const stored = localStorage.getItem('isAdmin'); if (stored === 'true') setIsAdmin(true); }
    } catch {}
  }, []);
  const [newGoal, setNewGoal] = useState({
    title: '',
    category: '',
    description: '',
    targetDate: '',
    priority: 'medium'
  });

  const goalCategories = [
    { id: 'health', name: 'Health & Wellness', icon: '🏥', color: 'from-green-100 to-emerald-100' },
    { id: 'fitness', name: 'Fitness & Movement', icon: '💪', color: 'from-blue-100 to-cyan-100' },
    { id: 'nutrition', name: 'Nutrition & Diet', icon: '🥗', color: 'from-orange-100 to-amber-100' },
    { id: 'lifestyle', name: 'Lifestyle & Habits', icon: '🌱', color: 'from-purple-100 to-violet-100' },
    { id: 'mental', name: 'Mental Wellbeing', icon: '🧠', color: 'from-pink-100 to-rose-100' },
    { id: 'skin', name: 'Skin Health', icon: '✨', color: 'from-teal-100 to-cyan-100' }
  ];

  const handleCreateGoal = () => {
    if (!newGoal.title.trim() || !newGoal.category) return;
    const goal = {
      id: Date.now(),
      title: newGoal.title.trim(),
      category: newGoal.category,
      description: newGoal.description.trim(),
      targetDate: newGoal.targetDate || '',
      priority: newGoal.priority || 'medium',
      progress: 0,
      status: 'active',
      steps: [],
      milestones: [25,50,75,100]
    };
    setDetailedGoals(prev => [goal, ...prev]);
    setShowNewGoalForm(false);
    setNewGoal({ title: '', category: '', description: '', targetDate: '', priority: 'medium' });
  };

  const handleEditGoal = (goal) => {
    setEditingGoal({
      ...goal,
      targetDate: goal.targetDate || ''
    });
    setShowEditGoalForm(true);
  };

  const handleUpdateGoal = () => {
    if (!editingGoal) return;
    setDetailedGoals(prev => prev.map(g => g.id === editingGoal.id ? { ...g, ...editingGoal } : g));
    setShowEditGoalForm(false);
    setEditingGoal(null);
  };

  const toggleStep = (goalId, stepId) => {
    setDetailedGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      const nextSteps = g.steps.map(s => s.id === stepId ? { ...s, completed: !s.completed } : s);
      const total = nextSteps.length;
      const completed = nextSteps.filter(s => s.completed).length;
      const progress = total > 0 ? Math.round((completed / total) * 100) : (g.progress || 0);
      return { ...g, steps: nextSteps, progress };
    }));
  };
  const addStep = (goalId) => {
    const text = (newStepText[goalId] || '').trim();
    if (!text) return;
    setDetailedGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      const nextId = (g.steps?.reduce((m,s)=>Math.max(m,s.id),0) || 0) + 1;
      const nextSteps = [...(g.steps || []), { id: nextId, text, completed: false }];
      const total = nextSteps.length;
      const completed = nextSteps.filter(s => s.completed).length;
      const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
      return { ...g, steps: nextSteps, progress };
    }));
    setNewStepText(prev => ({ ...prev, [goalId]: '' }));
  };
  const deleteGoal = (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    setDetailedGoals(prev => prev.filter(g => g.id !== goalId));
  };
  const markGoalCompleted = (goalId) => {
    setDetailedGoals(prev => prev.map(g => g.id === goalId ? { ...g, status: 'completed', progress: 100, completedDate: new Date().toISOString() } : g));
  };

  const getCategoryInfo = (categoryId) => {
    return goalCategories.find(cat => cat.id === categoryId) || goalCategories[0];
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-gray-400';
  };

  // Derived lists
  const currentGoals = detailedGoals.filter(g => g.status !== 'completed');
  const completedGoals = detailedGoals.filter(g => g.status === 'completed');

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-50 pt-20 px-3 sm:px-6 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <RouterLink to="/dashboard" className="text-gray-600 hover:text-gray-800 transition-colors" title="Back to Dashboard">
                <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </RouterLink>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-charcoal-800">Goals & Reflection</h1>
            </div>
            {/* Single + New button with dropdown */}
            <div className="relative">
              <button 
                onClick={() => setShowAddMenu(!showAddMenu)} 
                className="bg-pink-600 text-white px-4 py-2.5 rounded-lg hover:bg-pink-700 transition-colors text-sm font-medium"
              >
                + New
              </button>
              {showAddMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-30">
                  <button onClick={() => { setShowNewGoalForm(true); setShowAddMenu(false); }} className="w-full text-left px-4 py-3 text-sm text-charcoal-700 hover:bg-pink-50 rounded-t-lg">Goal</button>
                  <button onClick={() => { setShowAddWeekly(true); setShowAddMenu(false); }} className="w-full text-left px-4 py-3 text-sm text-charcoal-700 hover:bg-pink-50">Weekly Focus</button>
                  <button onClick={() => { setShowAddHabit(true); setShowAddMenu(false); }} className="w-full text-left px-4 py-3 text-sm text-charcoal-700 hover:bg-pink-50 rounded-b-lg">Habit</button>
                </div>
              )}
            </div>
          </div>
          
          {/* Admin Controls */}
          {isAdmin && (
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Admin</span>
              <button onClick={()=>{ localStorage.setItem('isAdmin','false'); setIsAdmin(false); }} className="bg-gray-200 text-charcoal-700 px-3 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm">Exit Admin</button>
            </div>
          )}
        </div>

        {/* Two-tab navigation */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'overview'
                ? 'bg-pink-500 text-white'
                : 'text-charcoal-600 hover:bg-pink-50'
            }`}
          >
            Goals & Habits
          </button>
          <button
            onClick={() => setActiveTab('reflection')}
            className={`flex-1 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'reflection'
                ? 'bg-pink-500 text-white'
                : 'text-charcoal-600 hover:bg-pink-50'
            }`}
          >
            Reflection
          </button>
        </div>

        {/* Celebration Modal */}
        {celebration && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full text-center">
              <div className="text-5xl sm:text-6xl mb-2">🎉</div>
              <h3 className="text-xl sm:text-2xl font-bold text-charcoal-900 mb-1">Milestone reached!</h3>
              <p className="text-sm sm:text-base text-charcoal-700 mb-4">{celebration.goalTitle} is {celebration.threshold}% complete. Amazing progress!</p>
              <button onClick={() => setCelebration(null)} className="w-full bg-green-600 text-white py-3 rounded-xl hover:bg-green-700">Keep Going</button>
            </motion.div>
          </div>
        )}

        {/* Add Weekly Goal Modal */}
        {showAddWeekly && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-lg w-full">
              <h3 className="text-xl font-semibold text-charcoal-800 mb-4">Add Weekly Goal</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={weeklyForm.title}
                    onChange={(e) => setWeeklyForm({ ...weeklyForm, title: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="e.g., Prep 4 dinners, Walk 5k steps daily"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1">Due Date (optional)</label>
                  <input
                    type="date"
                    value={weeklyForm.dueDate}
                    onChange={(e) => setWeeklyForm({ ...weeklyForm, dueDate: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={addWeeklyGoal} className="flex-1 bg-pink-600 text-white py-3 rounded-lg hover:bg-pink-700">Save</button>
                <button onClick={() => setShowAddWeekly(false)} className="flex-1 bg-gray-200 text-charcoal-700 py-3 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Habit Modal */}
        {showAddHabit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-lg w-full">
              <h3 className="text-xl font-semibold text-charcoal-800 mb-4">Add Habit</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1">Habit Name</label>
                  <input
                    type="text"
                    value={habitForm.name}
                    onChange={(e) => setHabitForm({ name: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Hydration, Sleep 7h, Walk 20 min"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={addHabit} className="flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700">Save</button>
                <button onClick={() => setShowAddHabit(false)} className="flex-1 bg-gray-200 text-charcoal-700 py-3 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* New Goal Form Modal */}
        {showNewGoalForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-charcoal-800 mb-6">Create New Goal</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Goal Title</label>
                  <input
                    type="text"
                    value={newGoal.title}
                    onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="e.g., Exercise 3x per week"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Category</label>
                  <select
                    value={newGoal.category}
                    onChange={(e) => setNewGoal({...newGoal, category: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="">Select a category</option>
                    {goalCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Description</label>
                  <textarea
                    value={newGoal.description}
                    onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    rows="3"
                    placeholder="Describe your goal and why it's important to you..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">Target Date</label>
                    <input
                      type="date"
                      value={newGoal.targetDate}
                      onChange={(e) => setNewGoal({...newGoal, targetDate: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">Priority</label>
                    <select
                      value={newGoal.priority}
                      onChange={(e) => setNewGoal({...newGoal, priority: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={handleCreateGoal} className="flex-1 bg-pink-600 text-white py-3 px-6 rounded-lg hover:bg-pink-700 transition-colors">Create Goal</button>
                <button onClick={() => setShowNewGoalForm(false)} className="flex-1 bg-gray-200 text-charcoal-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Goal Form Modal */}
        {showEditGoalForm && editingGoal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-charcoal-800 mb-6">Edit Goal</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Goal Title</label>
                  <input
                    type="text"
                    value={editingGoal.title}
                    onChange={(e) => setEditingGoal({...editingGoal, title: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="e.g., Exercise 3x per week"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Category</label>
                  <select
                    value={editingGoal.category}
                    onChange={(e) => setEditingGoal({...editingGoal, category: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="">Select a category</option>
                    {goalCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Description</label>
                  <textarea
                    value={editingGoal.description}
                    onChange={(e) => setEditingGoal({...editingGoal, description: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    rows="3"
                    placeholder="Describe your goal and why it's important to you..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">Target Date</label>
                    <input
                      type="date"
                      value={editingGoal.targetDate}
                      onChange={(e) => setEditingGoal({...editingGoal, targetDate: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">Priority</label>
                    <select
                      value={editingGoal.priority}
                      onChange={(e) => setEditingGoal({...editingGoal, priority: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">Progress (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editingGoal.progress}
                    onChange={(e) => setEditingGoal({...editingGoal, progress: parseInt(e.target.value) || 0})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    placeholder="Enter progress percentage (0-100)"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={handleUpdateGoal} className="flex-1 bg-pink-600 text-white py-3 px-6 rounded-lg hover:bg-pink-700 transition-colors">Update Goal</button>
                <button onClick={() => { setShowEditGoalForm(false); setEditingGoal(null); }} className="flex-1 bg-gray-200 text-charcoal-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Weight Entry Modal */}
        {showWeightModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold text-charcoal-800 mb-4">Log Weight</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1">Weight (kg)</label>
                  <input type="number" step="0.1" value={newWeight.value} onChange={(e) => setNewWeight({ ...newWeight, value: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="e.g., 68.5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1">Date</label>
                  <input type="date" value={newWeight.date} onChange={(e) => setNewWeight({ ...newWeight, date: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1">Notes (optional)</label>
                  <textarea value={newWeight.notes} onChange={(e) => setNewWeight({ ...newWeight, notes: e.target.value })} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" rows="2" placeholder="How are you feeling?" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={addWeightEntry} className="flex-1 bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700">Save</button>
                <button onClick={() => setShowWeightModal(false)} className="flex-1 bg-gray-200 text-charcoal-700 py-3 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ===== TAB 1: Goals & Habits ===== */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Weekly Focus */}
            <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-pink-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-charcoal-800">Weekly Focus</h2>
                <span className="text-xs text-charcoal-500">{weeklyGoalsV2.filter(g => g.done).length}/{weeklyGoalsV2.length} done</span>
              </div>
              {weeklyGoalsV2.length === 0 ? (
                <p className="text-sm text-charcoal-500">No weekly goals yet. Tap "+ New" to add one.</p>
              ) : (
                <div className="space-y-2">
                  {weeklyGoalsV2.slice(0,5).map((g) => (
                    <div key={g.id} className="flex items-center gap-3 p-2 rounded-lg border border-gray-100">
                      <input type="checkbox" checked={g.done} onChange={() => toggleWeeklyDone(g.id)} className="w-4 h-4 text-pink-600 rounded flex-shrink-0" />
                      <span className={`flex-1 text-sm ${g.done ? 'line-through text-gray-400' : 'text-charcoal-800'}`}>{g.title}</span>
                      <button onClick={() => deleteWeeklyGoal(g.id)} className="text-xs text-gray-400 hover:text-red-500">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Daily Habits */}
            <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-purple-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-charcoal-800">Daily Habits</h2>
                <button onClick={startNewWeek} className="text-xs bg-gray-100 text-charcoal-600 px-2.5 py-1.5 rounded-lg hover:bg-gray-200 transition-colors" title="Clear this week's checkmarks">
                  🔄 Reset
                </button>
              </div>
              {habitsV2.length === 0 ? (
                <p className="text-sm text-charcoal-500">No habits yet. Tap "+ New" to add one.</p>
              ) : (
                <div className="overflow-x-auto">
                  <div className="min-w-[480px]">
                    <div className="grid" style={{ gridTemplateColumns: `minmax(100px, 180px) repeat(7, 1fr)` }}>
                      <div></div>
                      {weekDates.map((d, i) => (
                        <div key={i} className="text-xs text-center text-charcoal-500 pb-2">
                          <div className="font-medium">{d.toLocaleDateString(undefined,{ weekday:'short'})}</div>
                          <div>{d.getDate()}</div>
                        </div>
                      ))}
                      {habitsV2.map(h => (
                        <React.Fragment key={h.id}>
                          <div className="py-2 pr-2 flex items-center justify-between border-t border-gray-100">
                            <span className="text-xs sm:text-sm text-charcoal-800 truncate">{h.name}</span>
                            <button onClick={() => deleteHabit(h.id)} className="text-xs text-gray-400 hover:text-red-500 ml-1 flex-shrink-0">✕</button>
                          </div>
                          {weekDates.map((d, i) => {
                            const key = `${h.id}_${fmt(d)}`;
                            const checked = !!habitChecks[key];
                            return (
                              <div key={i} className="border-t border-gray-100 flex items-center justify-center py-2">
                                <button
                                  onClick={() => toggleHabit(h.id, fmt(d))}
                                  className={`w-7 h-7 rounded-full border-2 text-xs ${checked ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-300 text-gray-400'} hover:shadow transition-all`}
                                  aria-label={`Toggle ${h.name} on ${d.toDateString()}`}
                                >
                                  {checked ? '✓' : ''}
                                </button>
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Active Goals - Collapsed cards */}
            {currentGoals.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-charcoal-800 mb-3">Active Goals</h2>
                <div className="space-y-3">
                  {currentGoals.map((goal) => {
                    const categoryInfo = getCategoryInfo(goal.category);
                    const computedProgress = getGoalProgress(goal);
                    const isExpanded = expandedGoals[goal.id];
                    return (
                      <div key={goal.id} className="bg-white rounded-lg shadow-sm border border-pink-100 overflow-hidden">
                        {/* Collapsed header - always visible */}
                        <button
                          onClick={() => toggleGoalExpand(goal.id)}
                          className="w-full text-left p-4 flex items-center gap-3"
                        >
                          <span className="text-xl flex-shrink-0">{categoryInfo.icon}</span>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm sm:text-base font-semibold text-charcoal-900 truncate">{goal.title}</h3>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(computedProgress)}`}
                                style={{ width: `${computedProgress}%` }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-sm font-bold text-charcoal-700 flex-shrink-0">{computedProgress}%</span>
                          <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                            {goal.description && <p className="text-sm text-charcoal-600 mb-3">{goal.description}</p>}
                            
                            <div className="flex items-center gap-2 mb-4 text-xs text-charcoal-500">
                              {goal.targetDate && <span>Target: {new Date(goal.targetDate).toLocaleDateString()}</span>}
                              <span className={`px-2 py-0.5 rounded-full font-medium ${
                                goal.priority === 'high' ? 'bg-red-100 text-red-700' :
                                goal.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>{goal.priority}</span>
                            </div>

                            {/* Action Steps */}
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-charcoal-700 mb-2">Action Steps</h4>
                              <div className="space-y-1.5">
                                {goal.steps.map((step) => (
                                  <div key={step.id} className="flex items-center gap-2">
                                    <input type="checkbox" checked={step.completed} onChange={() => toggleStep(goal.id, step.id)} className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500" />
                                    <span className={`text-sm flex-1 ${step.completed ? 'line-through text-gray-400' : 'text-charcoal-700'}`}>{step.text}</span>
                                  </div>
                                ))}
                                <div className="flex gap-2 mt-2">
                                  <input
                                    type="text"
                                    value={newStepText[goal.id] || ''}
                                    onChange={(e) => setNewStepText(prev => ({ ...prev, [goal.id]: e.target.value }))}
                                    className="flex-1 p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                                    placeholder="Add a step..."
                                  />
                                  <button onClick={() => addStep(goal.id)} className="bg-pink-600 text-white px-3 py-2 rounded-lg hover:bg-pink-700 text-sm">Add</button>
                                </div>
                              </div>
                            </div>

                            {/* Goal actions */}
                            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                              <button onClick={() => handleEditGoal(goal)} className="text-xs text-pink-600 hover:text-pink-700 px-2 py-1 rounded hover:bg-pink-50">Edit</button>
                              <button onClick={() => markGoalCompleted(goal.id)} className="text-xs text-green-600 hover:text-green-700 px-2 py-1 rounded hover:bg-green-50">Mark Complete</button>
                              <button onClick={() => deleteGoal(goal.id)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 ml-auto">Delete</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Completed Goals - collapsible */}
            {completedGoals.length > 0 && (
              <details className="group">
                <summary className="text-sm font-medium text-charcoal-600 cursor-pointer hover:text-charcoal-800 list-none flex items-center gap-2">
                  <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  Completed ({completedGoals.length})
                </summary>
                <div className="mt-3 space-y-2">
                  {completedGoals.map((goal) => {
                    const categoryInfo = getCategoryInfo(goal.category);
                    return (
                      <div key={goal.id} className="bg-white rounded-lg p-3 shadow-sm border border-green-200 flex items-center gap-3">
                        <span className="text-lg">{categoryInfo.icon}</span>
                        <span className="flex-1 text-sm text-charcoal-700">{goal.title}</span>
                        <span className="text-xs text-green-600 font-medium">✓ {new Date(goal.completedDate).toLocaleDateString()}</span>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}

            {currentGoals.length === 0 && completedGoals.length === 0 && (
              <div className="bg-white rounded-lg p-8 text-center shadow-sm">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className="text-lg font-semibold text-charcoal-800 mb-1">No goals yet</h3>
                <p className="text-sm text-charcoal-500">Tap "+ New" to create your first goal.</p>
              </div>
            )}
          </div>
        )}

        {/* ===== TAB 2: Reflection ===== */}
        {activeTab === 'reflection' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-5 shadow-sm border border-purple-100">
              <h2 className="text-lg font-semibold text-charcoal-800 mb-1">Reflection Prompts</h2>
              <p className="text-sm text-charcoal-600">Choose a prompt that resonates with you today.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { category: 'Wellness', icon: '🌿', color: 'from-green-100 to-emerald-100', borderColor: 'border-green-300', prompts: ['What have I done today to promote my overall wellness?', 'How am I nourishing my body with healthy choices?', 'What wellness goals am I working towards?'] },
                { category: 'Mindfulness', icon: '🧘‍♀️', color: 'from-purple-100 to-violet-100', borderColor: 'border-purple-300', prompts: ['When did I feel most present today?', 'What am I grateful for in this moment?', 'How can I bring more mindfulness into my daily routine?'] },
                { category: 'Skin Health', icon: '✨', color: 'from-pink-100 to-rose-100', borderColor: 'border-pink-300', prompts: ['How is my skin feeling today?', 'What skincare practices are serving me well?', 'What triggers have I noticed affecting my skin?'] },
                { category: 'Nutrition', icon: '🥗', color: 'from-orange-100 to-amber-100', borderColor: 'border-orange-300', prompts: ['What nourishing foods did I enjoy today?', 'How does my body feel after eating?', 'What food choices made me feel energized?'] },
                { category: 'Movement', icon: '💪', color: 'from-blue-100 to-cyan-100', borderColor: 'border-blue-300', prompts: ['How did I move my body today?', 'What type of movement brings me joy?', 'How does movement make me feel?'] },
                { category: 'Self-Care', icon: '💝', color: 'from-rose-100 to-pink-100', borderColor: 'border-rose-300', prompts: ['What act of self-care did I prioritize today?', 'How am I honoring my needs and boundaries?', 'What makes me feel cared for and loved?'] }
              ].map((cat) => (
                <div key={cat.category} className={`bg-gradient-to-br ${cat.color} p-4 rounded-lg border ${cat.borderColor}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{cat.icon}</span>
                    <h3 className="text-base font-semibold text-charcoal-900">{cat.category}</h3>
                  </div>
                  <div className="space-y-1.5">
                    {cat.prompts.map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => openPromptModal(cat.category, prompt)}
                        className="w-full text-left bg-white/80 hover:bg-white p-2.5 rounded-lg text-xs sm:text-sm text-charcoal-700 transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* History */}
            <div className="flex justify-center">
              <button 
                onClick={() => setShowReflectionHistory(!showReflectionHistory)}
                className="bg-purple-600 text-white px-6 py-2.5 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                View History ({reflectionHistory.length})
              </button>
            </div>

            {showReflectionHistory && (
              <div className="bg-white rounded-lg p-5 shadow-sm border border-purple-100">
                <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Your Reflections</h3>
                {reflectionHistory.length === 0 ? (
                  <p className="text-center text-charcoal-500 py-6 text-sm">No saved reflections yet. Choose a prompt above to get started!</p>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {reflectionHistory.map((entry) => (
                      <div key={entry.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-xs font-medium text-purple-700">
                              {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{entry.category}</p>
                          </div>
                          <button onClick={() => { if (window.confirm('Delete this reflection?')) { setReflectionHistory(reflectionHistory.filter(e => e.id !== entry.id)); }}} className="text-red-400 hover:text-red-600 text-xs">🗑️</button>
                        </div>
                        <p className="text-xs text-gray-500 italic mb-1">{entry.prompt}</p>
                        <p className="text-sm text-charcoal-700 whitespace-pre-wrap">{entry.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Reflection Modal */}
        {showReflectionModal && selectedPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h3 className="text-xl font-semibold text-charcoal-800">{selectedPrompt.category}</h3>
                  <p className="text-purple-600 mt-1 font-medium text-sm">{selectedPrompt.prompt}</p>
                </div>
                <button onClick={() => setShowReflectionModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>
              <textarea
                value={promptAnswer}
                onChange={(e) => setPromptAnswer(e.target.value)}
                placeholder="Take your time... what comes to mind?"
                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-h-[180px] mb-4"
                autoFocus
              />
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowReflectionModal(false)} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
                <button onClick={saveReflectionEntry} className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium">Save</button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyGoals;
